/* eslint-disable id-blacklist */

import { gql } from '@apollo/client';
import * as Apollo from '@apollo/client';

import type * as Types from './operations';

const defaultOptions = {};
export const AmericanFootballFragmentDoc = gql`
  fragment AmericanFootball on AmericanFootballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      timeElapsed
      timeRemaining
    }
    quarterScores {
      period
      score {
        home
        away
      }
    }
  }
`;
export const BasketballFragmentDoc = gql`
  fragment Basketball on BasketballFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      segment
      timeElapsed
      timeRemaining
    }
    periodScores {
      period
      segment
      score {
        home
        away
      }
    }
  }
`;
export const ESportsFragmentDoc = gql`
  fragment ESports on EsportsFixture {
    id
    score {
      home
      away
    }
  }
`;
export const IceHockeyFragmentDoc = gql`
  fragment IceHockey on IceHockeyFixture {
    id
    score {
      home
      away
    }
    clock {
      period
      timeElapsed
      timeRemaining
    }
    periodScores {
      period
      score {
        home
        away
      }
    }
  }
`;
export const FootballGameStatsFragmentDoc = gql`
  fragment FootballGameStats on FootballGameStats {
    corners
    goals
    totalCards
    shotsOnTarget
    totalShots
  }
`;
export const FootballTeamStatsFragmentDoc = gql`
  fragment FootballTeamStats on FootballFixture {
    stats(
      periods: [
        { periodStatus: FULL }
        { period: EXTRA, periodStatus: INPLAY_FIRST_HALF }
        { period: EXTRA, periodStatus: INPLAY_SECOND_HALF }
        { period: REGULAR, periodStatus: FULL }
        { period: REGULAR, periodStatus: INPLAY_FIRST_HALF }
        { period: REGULAR, periodStatus: INPLAY_SECOND_HALF }
      ]
    ) {
      period
      periodStatus
      home {
        ...FootballGameStats
      }
      away {
        ...FootballGameStats
      }
      both {
        ...FootballGameStats
      }
    }
  }
  ${FootballGameStatsFragmentDoc}
`;
export const FootballFragmentDoc = gql`
  fragment Football on FootballFixture {
    id
    away {
      name
    }
    home {
      name
    }
    score {
      home
      away
    }
    duration {
      period
      status
      clock {
        minute
        second
        roundMinute
      }
    }
    status
    penaltyShootout {
      firstTeamToShoot
      penaltyFormat
      nextTeamToShoot
      penaltyScores {
        penaltyNumber
        side
        shotResult
      }
    }
    ...FootballTeamStats @include(if: $includeTeamStats)
  }
  ${FootballTeamStatsFragmentDoc}
`;
export const TennisFragmentDoc = gql`
  fragment Tennis on TennisMatch {
    id
    teamAScore
    teamBScore
    currentSet {
      teamAScore
      teamBScore
      currentGame {
        teamAScore
        teamBScore
        teamServing
        type
      }
    }
    previousSets {
      teamAScore
      teamBScore
    }
    status {
      reason
      status
    }
  }
`;
export const LiveDataDocument = gql`
  query LiveData(
    $americanFootballEventIds: [ID!]!
    $basketballEventIds: [ID!]!
    $footballEventIds: [ID!]!
    $eSportsEventIds: [ID!]!
    $iceHockeyEventIds: [ID!]!
    $tennisEventIds: [ID!]!
    $includeTeamStats: Boolean!
  ) {
    americanFootball {
      fixture(ids: $americanFootballEventIds) {
        ...AmericanFootball
      }
    }
    basketball {
      fixture(ids: $basketballEventIds) {
        ...Basketball
      }
    }
    football {
      fixture(ids: $footballEventIds) {
        ...Football
      }
    }
    esports {
      fixture(ids: $eSportsEventIds) {
        ...ESports
      }
    }
    iceHockey {
      fixture(ids: $iceHockeyEventIds) {
        ...IceHockey
      }
    }
    tennis {
      match(ids: $tennisEventIds) {
        ...Tennis
      }
    }
  }
  ${AmericanFootballFragmentDoc}
  ${BasketballFragmentDoc}
  ${FootballFragmentDoc}
  ${ESportsFragmentDoc}
  ${IceHockeyFragmentDoc}
  ${TennisFragmentDoc}
`;

/**
 * __useLiveDataQuery__
 *
 * To run a query within a React component, call `useLiveDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useLiveDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useLiveDataQuery({
 *   variables: {
 *      americanFootballEventIds: // value for 'americanFootballEventIds'
 *      basketballEventIds: // value for 'basketballEventIds'
 *      footballEventIds: // value for 'footballEventIds'
 *      eSportsEventIds: // value for 'eSportsEventIds'
 *      iceHockeyEventIds: // value for 'iceHockeyEventIds'
 *      tennisEventIds: // value for 'tennisEventIds'
 *      includeTeamStats: // value for 'includeTeamStats'
 *   },
 * });
 */
export function useLiveDataQuery(
  baseOptions: Apollo.QueryHookOptions<Types.LiveDataQuery, Types.LiveDataQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<Types.LiveDataQuery, Types.LiveDataQueryVariables>(
    LiveDataDocument,
    options
  );
}
export function useLiveDataLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<Types.LiveDataQuery, Types.LiveDataQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<Types.LiveDataQuery, Types.LiveDataQueryVariables>(
    LiveDataDocument,
    options
  );
}
export type LiveDataQueryHookResult = ReturnType<typeof useLiveDataQuery>;
export type LiveDataLazyQueryHookResult = ReturnType<typeof useLiveDataLazyQuery>;
export type LiveDataQueryResult = Apollo.QueryResult<
  Types.LiveDataQuery,
  Types.LiveDataQueryVariables
>;
